import { useCallback, useMemo, useState } from 'react';

import {
  useGetUserTrainingAccessOutgoing,
  usePostUserTrainingAccessOutgoing,
} from '../../../../redux/api/userTrainingAccessApi';
import type { IUsersApiUserInList } from '../../../../redux/api/users/users-api.types';
import {
  useGetMyDetails,
  useGetUserSubscribers,
} from '../../../../redux/api/users/usersApi';
import { IUser } from '../../../../types/interfaces/user';
import type { IUserTrainingAccessSectionKey } from '../../../../types/interfaces/user-training-access';
import {
  buildPostBodyForSection,
  computeIdDelta,
  mergeSubscribersWithAccessRows,
  rowsToSortedIUsers,
} from './access-settings-helpers';

export function useAccessSettings() {
  const { data: me, isLoading: isLoadingMe } = useGetMyDetails();
  const userId = me?.id;
  const skip = !userId;

  const {
    data: accessData,
    isLoading: isLoadingAccess,
    isFetching: isFetchingAccess,
    isError: isAccessError,
    error: accessError,
    refetch: refetchAccess,
  } = useGetUserTrainingAccessOutgoing(undefined, { skip });

  const {
    data: subscribers = [],
    isLoading: isLoadingSubscribers,
    isFetching: isFetchingSubscribers,
    isError: isSubscribersError,
    error: subscribersError,
    refetch: refetchSubscribers,
  } = useGetUserSubscribers(userId ?? '', { skip });

  const [postAccess, { isLoading: isPosting }] =
    usePostUserTrainingAccessOutgoing();

  const [dialogSection, setDialogSection] =
    useState<IUserTrainingAccessSectionKey | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{
    section: IUserTrainingAccessSectionKey;
    user: IUser;
  } | null>(null);

  const rowsBySection = useMemo(
    () => ({
      createTraining: accessData?.createTraining ?? [],
      sharePrograms: accessData?.sharePrograms ?? [],
      shareTrainings: accessData?.shareTrainings ?? [],
    }),
    [accessData],
  );

  const displayUsersBySection = useMemo(
    () => ({
      createTraining: rowsToSortedIUsers(rowsBySection.createTraining),
      sharePrograms: rowsToSortedIUsers(rowsBySection.sharePrograms),
      shareTrainings: rowsToSortedIUsers(rowsBySection.shareTrainings),
    }),
    [rowsBySection],
  );

  const modalUsersBySection = useMemo(() => {
    const subs = subscribers as IUsersApiUserInList[];
    return {
      createTraining: mergeSubscribersWithAccessRows(
        subs,
        rowsBySection.createTraining,
      ),
      sharePrograms: mergeSubscribersWithAccessRows(
        subs,
        rowsBySection.sharePrograms,
      ),
      shareTrainings: mergeSubscribersWithAccessRows(
        subs,
        rowsBySection.shareTrainings,
      ),
    };
  }, [subscribers, rowsBySection]);

  const openDialog = useCallback((section: IUserTrainingAccessSectionKey) => {
    setDialogSection(section);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogSection(null);
  }, []);

  const handleModalSave = useCallback(
    async (section: IUserTrainingAccessSectionKey, nextIds: string[]) => {
      const prevIds = rowsBySection[section].map((r) => r.id);
      const { add, remove } = computeIdDelta(prevIds, nextIds);
      if (add.length === 0 && remove.length === 0) {
        closeDialog();
        return;
      }
      const body = buildPostBodyForSection(section, add, remove);
      if (Object.keys(body).length === 0) {
        closeDialog();
        return;
      }
      await postAccess(body).unwrap();
      closeDialog();
    },
    [closeDialog, postAccess, rowsBySection],
  );

  const handleConfirmRevoke = useCallback(async () => {
    if (!removeTarget) {
      return;
    }
    const { section, user } = removeTarget;
    const body = buildPostBodyForSection(section, [], [user.id]);
    await postAccess(body).unwrap();
    setRemoveTarget(null);
  }, [postAccess, removeTarget]);

  const isLoading =
    isLoadingMe ||
    (!skip && (isLoadingAccess || isLoadingSubscribers)) ||
    isFetchingAccess ||
    isFetchingSubscribers;

  const isError = isAccessError || isSubscribersError;
  const errors = [
    ...(isAccessError && accessError ? [accessError] : []),
    ...(isSubscribersError && subscribersError ? [subscribersError] : []),
  ];

  return {
    userId,
    isLoading,
    isError,
    errors,
    isPosting,
    refetch: () => {
      void refetchAccess();
      void refetchSubscribers();
    },
    rowsBySection,
    displayUsersBySection,
    modalUsersBySection,
    dialogSection,
    openDialog,
    closeDialog,
    handleModalSave,
    removeTarget,
    setRemoveTarget,
    handleConfirmRevoke,
  };
}
